// serviceFlavorsModel.js
// Pure pricing layer for the Service Flavors builder. Wraps the existing
// estimate engine (computeTier) and adds drive-time / start-up / tax.
// NO React. NO mutation of DEFAULT_ESTIMATE_SETTINGS.

import { computeTier } from '../estimateEngine';

export const BLOW_HRS = 1.5;        // labor hours per blow & go visit
export const PRUNE_FACTOR = 0.88;   // natural pruning multiplies maintenance labor
export const FERT_PER_VISIT = 950;  // flat $/visit (placeholder default)
export const ARBORIST_PER_TREE = 275;       // handoff default
export const ARBORIST_PER_PALM = 350;       // PLACEHOLDER — tune later

// Visits/year ladder (descending) for non-maintenance services. Index `fi`.
export const FREQS = [
  { v: 52, f: 'Weekly' }, { v: 39, f: '3×/mo' }, { v: 26, f: 'Bi-weekly' }, { v: 24, f: '2×/mo' },
  { v: 12, f: 'Monthly' }, { v: 6, f: '6×/yr' }, { v: 4, f: '4×/yr' }, { v: 3, f: '3×/yr' },
  { v: 2, f: '2×/yr' }, { v: 1, f: '1×/yr' },
];

// Irrigation repair coverage — annual allowance added to irrigation cost.
export const COV = [
  { label: 'Inspections only', add: 0, note: 'Scheduled inspections & adjustments only — repairs billed as they occur.' },
  { label: 'To valve', add: 1800, note: 'Covers all repairs up to the valve, included in the monthly fee.' },
  { label: 'To mainline', add: 3600, note: 'Covers all repairs up to the mainline, included in the monthly fee.' },
];

export const STARTUP_KEYS = ['cleanup', 'mulch', 'audit', 'plant'];

const num = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
export const roundTo = (n, step) => Math.round(num(n) / step) * step;
export const markup = (cost, marginPct) => {
  const m = clamp(num(marginPct), 0, 95);
  return num(cost) / (1 - m / 100);
};
export const markupMonthly = (cost, marginPct) => markup(cost, marginPct) / 12;

export const FLAVOR_ROLES = ['Maint', 'Irrigation', 'Herbicide', 'Arborist', 'Fert'];

// Flavor-local Arborist cost: (trees×perTree + palms×perPalm) × visits/yr.
export function arboristCost(st = {}) {
  const visits = FREQS[clamp(num(st.fi, 9), 0, FREQS.length - 1)].v;
  return (num(st.trees) * ARBORIST_PER_TREE + num(st.palms) * ARBORIST_PER_PALM) * visits;
}

const areaTreeCount = (a = {}) => num(a.treeCount ?? a.plantDensity?.treeCount);
const areaPalmCount = (a = {}) => num(a.palmCount ?? a.plantDensity?.palmCount);

// Seed per-role display data from the takeoff. Labor roles expose real annual
// hours (for display only — COST is derived by passing real service lines to
// computeTier). Arborist seeds tree/palm COUNTS from areas (NOT hours).
export function seedFromTakeoff(services = [], areas = []) {
  const byRole = {};
  (services || []).forEach((s) => { byRole[s.role] = s; });

  const topAreas = (areas || []).filter((a) => !a.parentAreaId);
  const trees = topAreas.reduce((sum, a) => sum + areaTreeCount(a), 0);
  const palms = topAreas.reduce((sum, a) => sum + areaPalmCount(a), 0);

  const laborSeed = (role) => {
    const s = byRole[role];
    if (!s) return { enabled: false, subcontracted: false, annualHours: 0 };
    const subcontracted = s.delivery === 'subcontracted';
    const annualHours = subcontracted ? 0 : (s.tasks || []).reduce((sum, t) => sum + num(t.annualHours), 0);
    return { enabled: !!s.enabled, subcontracted, annualHours };
  };

  return {
    Maint: laborSeed('Maint'),
    Irrigation: laborSeed('Irrigation'),
    Herbicide: laborSeed('Herbicide'),
    Arborist: { trees, palms, annualHours: 0 },
    Fert: { annualHours: 0 },
  };
}

const freqObj = (visitsPerYear, label) => ({
  label: label || `${visitsPerYear}x / yr`,
  visitsPerYear,
  family: 'yr',
});

// Engine roles priced via computeTier (labor or subcontracted). Arborist + Fert
// are flavor-local (count/flat) and intentionally excluded here.
const ENGINE_ROLES = ['Maint', 'Irrigation', 'Herbicide'];

// Build an engine-shaped estimate from the REAL takeoff service lines, applying
// per-flavor knobs (hoursPct, freqMult) to each task's hours. computeService then
// sums each task's adjusted annualHours at its own frequency -> matches legacy.
export function adjustRealServices(flavor, takeoffServices = []) {
  const svc = flavor.svc || {};
  const byRole = {};
  (takeoffServices || []).forEach((s) => { byRole[s.role] = s; });
  const includedServices = [];
  const services = [];

  ENGINE_ROLES.forEach((role) => {
    const st = svc[role];
    const real = byRole[role];
    if (!st || st.mode === 'off' || !real) return;        // off / no takeoff line -> skip
    if (st.mode !== 'in') return;                         // 'extra' handled separately by computeFlavor
    includedServices.push(role);

    if (real.delivery === 'subcontracted') {
      services.push({ role, name: real.name || role, enabled: true,
        delivery: 'subcontracted', tasks: [], sub: { ...real.sub } });
      return;
    }

    const scale = (num(st.hoursPct, 100) / 100) * num(st.freqMult, 1);
    const scaleTasks = (tasks = []) => tasks.map((t) => ({
      ...t,
      hoursPerVisit: num(t.hoursPerVisit) * (num(st.hoursPct, 100) / 100), // per-visit reflects hours% only
      annualHours: num(t.annualHours) * scale,                            // annual reflects hours% AND freq
    }));
    const routineTasks = scaleTasks(real.routineTasks);
    const periodicTasks = scaleTasks(real.periodicTasks);
    const tasks = (real.routineTasks || real.periodicTasks)
      ? [...routineTasks, ...periodicTasks]
      : scaleTasks(real.tasks);
    services.push({ role, name: real.name || role, enabled: true, delivery: 'labor',
      routineTasks, periodicTasks, tasks });
  });

  return { services, includedServices, rateOverrides: {} };
}

// Unused this pass — retained for the deferred drive-time pass (superseded by
// adjustRealServices, which feeds REAL takeoff tasks to computeTier).
// Build a per-flavor estimate consumable by computeTier. Engine roles only
// (Maint/Irrigation/Herbicide/Arborist). Fert is excluded here (flavor-local).
export function synthesizeFlavorEstimate(flavor, takeoffServices = []) {
  const svc = flavor.svc || {};
  const byRole = {};
  takeoffServices.forEach((s) => { byRole[s.role] = s; });
  const includedServices = [];
  const services = [];

  const maint = svc.Maint || {};
  if (maint.mode === 'in') {
    includedServices.push('Maint');
    const days = num(maint.days, 1);
    const pruneMult = maint.prune === 'natural' ? PRUNE_FACTOR : 1;
    const hpv = num(maint.hrsPerVisit) * pruneMult;
    services.push({
      role: 'Maint', name: 'Maintenance', enabled: true, delivery: 'labor',
      tasks: [{ serviceCode: 'flavor_maint', label: 'Maintenance', ratePerHour: 0,
        quantity: 0, coverage: 1, frequency: freqObj(days * 52, `${days}×/wk`),
        hoursPerVisit: hpv, annualHours: hpv * days * 52 }],
    });
  }
  // Maint 'extra' is intentionally NOT pushed (excluded from includedServices;
  // its add-on fee is computed separately in computeFlavor later).

  [['Irrigation', svc.Irrigation], ['Herbicide', svc.Herbicide], ['Arborist', svc.Arborist]]
    .forEach(([role, st]) => {
      if (!st || st.mode === 'off') return;
      const visits = FREQS[clamp(num(st.fi, 4 /* default Monthly */), 0, FREQS.length - 1)].v;
      const sub = byRole[role];
      const isSub = sub && sub.delivery === 'subcontracted';
      const built = isSub
        ? { role, name: role, enabled: true, delivery: 'subcontracted',
            tasks: [], sub: { ...sub.sub, frequency: freqObj(visits) } }
        : { role, name: role, enabled: true, delivery: 'labor',
            tasks: [{ serviceCode: `flavor_${role.toLowerCase()}`, label: role, ratePerHour: 0,
              quantity: 0, coverage: 1, frequency: freqObj(visits),
              hoursPerVisit: num(st.hrsPerVisit), annualHours: num(st.hrsPerVisit) * visits }] };
      if (st.mode === 'in') { includedServices.push(role); services.push(built); }
    });

  return { services, includedServices, rateOverrides: {} };
}

// Unused this pass — retained for the deferred drive-time pass.
// Sum trips across included services. Maint trips = visits + blowGo.
export function tripsForFlavor(flavor) {
  const svc = flavor.svc || {};
  let trips = 0;
  if (svc.Maint?.mode === 'in') {
    trips += num(svc.Maint.days, 1) * 52 + num(svc.Maint.blowGo, 0);
  }
  [['Irrigation', svc.Irrigation], ['Herbicide', svc.Herbicide], ['Arborist', svc.Arborist], ['Fert', svc.Fert]]
    .forEach(([, st]) => {
      if (st && st.mode === 'in') trips += FREQS[clamp(num(st.fi, 4), 0, FREQS.length - 1)].v;
    });
  return trips;
}

export function computeFlavor(flavor, globals = {}, settings = {}, takeoffServices = []) {
  const svc = flavor.svc || {};
  const margin = num(globals.margin, 50);

  // 1) Labor + sub cost via the existing engine, from REAL service lines.
  const estimate = adjustRealServices(flavor, takeoffServices);
  const tier = { includedServices: estimate.includedServices, marginPct: margin };
  const tierResult = computeTier(estimate, tier, settings);
  let cost = tierResult.totalCost;
  const hoursByRole = { ...tierResult.hoursByRole };

  // 2) Maintenance blow & go extra labor (BLOW_HRS each) — engine doesn't know it.
  if (svc.Maint?.mode === 'in' && num(svc.Maint.blowGo) > 0) {
    const blowHours = num(svc.Maint.blowGo) * BLOW_HRS;
    hoursByRole.Maint = (hoursByRole.Maint || 0) + blowHours;
    cost += blowHours * num(settings.laborRates?.Maint, 38.5);
  }

  // 3) Irrigation repair coverage allowance.
  if (svc.Irrigation?.mode === 'in') cost += COV[clamp(num(svc.Irrigation.cov, 0), 0, COV.length - 1)].add;

  // 4) Arborist — flavor-local, count-based (trees/palms × rate × visits).
  if (svc.Arborist?.mode === 'in') cost += arboristCost(svc.Arborist);

  // 5) Fertilization — flavor-local flat $/visit.
  if (svc.Fert?.mode === 'in') cost += FERT_PER_VISIT * FREQS[clamp(num(svc.Fert.fi, 9), 0, FREQS.length - 1)].v;

  // 6) Drive-time DEFERRED this pass — no trips term.

  // 7) Price.
  const price = markup(cost, margin);

  // 8) Start-up (one-time, marked up).
  const startupCost = STARTUP_KEYS.reduce((s, k) => s + (flavor.startup?.[k]?.on ? num(flavor.startup[k].cost) : 0), 0);
  const startupPrice = roundTo(markup(startupCost, margin), 25);

  // 9) Optional add-ons (mode==='extra') — own marked-up monthly fee.
  const extras = [];
  const roleExtraCost = (role, st) => {
    if (role === 'Arborist') return arboristCost(st);
    if (role === 'Fert') return FERT_PER_VISIT * FREQS[clamp(num(st.fi, 9), 0, FREQS.length - 1)].v;
    // labor/sub role: price its real service alone through computeTier
    const solo = adjustRealServices({ svc: { [role]: { ...st, mode: 'in' } } }, takeoffServices);
    let c = computeTier(solo, { includedServices: solo.includedServices, marginPct: 0 }, settings).totalCost;
    if (role === 'Irrigation') c += COV[clamp(num(st.cov, 0), 0, COV.length - 1)].add;
    return c;
  };
  ['Maint', 'Irrigation', 'Herbicide', 'Arborist', 'Fert'].forEach((role) => {
    const st = svc[role];
    if (st && st.mode === 'extra') {
      extras.push({ role, feeMonthly: roundTo(markupMonthly(roleExtraCost(role, st), margin), 5) });
    }
  });

  return { cost, hoursByRole, price, monthly: price / 12, startupPrice, extras };
}
